<template>
  <div>
    <div class="dialog-content">
      <!--
      基本設定-->
      <div class="caption mb-x2">基本設定</div>
      <!-- メッセージ名-->
      <div class="setting-content">
        <Panel>
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="メッセージ名"></FormItemLabel>
              <el-tooltip placement="top">
                <div slot="content">メッセージの要約をタイトルとして入力してください。 例: 商品A 2018-01-11当選分</div>
                <i class="mdi mdi-help-circle"></i>
              </el-tooltip>
            </div>
            <div class="input">
              <el-input
                placeholder="メッセージ名"
                maxlength="100"
                type="text"
                tabindex="1"
                v-model="message.name"
                :class="{ success: $v.message.name.required && !$v.message.name.$invalid }"
                :disabled="isFinished || canNotEditInstantwin"
              ></el-input>
            </div>
            <div class="input-invalid-text" v-if="!$v.message.name.required && $v.message.name.$error">
              メッセージ名を入力してください。
            </div>
          </div>
        </Panel>
      </div>
      <!-- 配信期間-->
      <div class="setting-content">
        <Panel>
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="配信期間" optional></FormItemLabel>
              <el-tooltip placement="top">
                <div slot="content">
                  メッセージの配信期間を指定してください。何も指定しない場合、配信期間 = キャンペーン期間となります。
                </div>
                <i class="mdi mdi-help-circle"></i>
              </el-tooltip>
            </div>
            <div class="input mb-x2">
              <el-date-picker
                placeholder="開始日"
                type="date"
                value-format="yyyy-MM-dd"
                :picker-options="datePickerOption"
                prefix-icon="mdi mdi-calendar-today"
                :editable="false"
                ref="startDate"
                tabindex="2"
                v-model="startDate"
                :disabled="isFinished || canNotEditInstantwin"
              ></el-date-picker>
              <el-time-select
                class="ml-x2"
                placeholder="開始時間"
                :picker-options="timePickerOption"
                prefix-icon="mdi mdi-clock"
                :editable="false"
                ref="startTime"
                tabindex="3"
                v-model="startTime"
                :disabled="isFinished || canNotEditInstantwin"
              ></el-time-select
              ><span class="ml-x2">以降</span>
              <div class="input-invalid-text" v-if="errors.notInputStartDate">開始日を入力してください。</div>
              <div class="input-invalid-text" v-if="errors.lessThanCurrentTime">
                開始日時は、現在日時より後を指定してください。
              </div>
            </div>
            <div class="input">
              <el-date-picker
                placeholder="終了日"
                type="date"
                value-format="yyyy-MM-dd"
                :picker-options="datePickerOption"
                prefix-icon="mdi mdi-calendar-today"
                :editable="false"
                ref="endDate"
                tabindex="4"
                v-model="endDate"
                :disabled="isFinished || canNotEditInstantwin"
              ></el-date-picker>
              <el-time-select
                class="ml-x2"
                placeholder="終了時間"
                :picker-options="timePickerOption"
                prefix-icon="mdi mdi-clock"
                :editable="false"
                ref="endTime"
                tabindex="5"
                v-model="endTime"
                :disabled="isFinished || canNotEditInstantwin"
              ></el-time-select
              ><span class="ml-x2">未満</span>
              <div class="input-invalid-text" v-if="errors.notInputEndDate">終了日を入力してください。</div>
              <div class="input-invalid-text" v-if="errors.lessThanStartTime">
                終了日時は、開始日時より後を指定してください。
              </div>
            </div>
          </div>
          <!-- 1日の切り替え時間-->
          <div class="input-container" v-if="message.messageType === 'win'">
            <div class="title">
              <FormItemLabel label="1日の切り替え時間"></FormItemLabel>
              <el-tooltip placement="top">
                <div slot="content">1日の当選数をリセットする時間を設定できます。</div>
                <i class="mdi mdi-help-circle"></i>
              </el-tooltip>
            </div>
            <el-time-select
              placeholder="時間"
              :picker-options="timePickerOption"
              prefix-icon="mdi mdi-clock"
              :editable="false"
              tabindex="6"
              v-model="message.dailyWinnerChangeTime"
              :disabled="isFinished || canNotEditInstantwin"
            ></el-time-select>
          </div>
        </Panel>
      </div>
      <!-- キーワード指定-->
      <div class="setting-content">
        <Panel>
          <div class="input-container">
            <FormItemLabel label="キャンペーンポストに以下のキーワードを含む" optional></FormItemLabel>
            <div class="description">
              キャンペーンポストに、以下のキーワードが含まれる場合、メッセージが配信されます。
            </div>
            <div class="input">
              <tag-input
                :tags="message.entryKeyword"
                add-tag-text="キーワード追加"
                :onChange="handleKeywordsChange"
                tabindex="7"
                :disabled="isFinished || canNotEditInstantwin"
              ></tag-input>
            </div>
          </div>
          <div class="input-container" v-if="isIncludeQuoteTweets">
            <FormItemLabel :label="quoteKeywordText" optional></FormItemLabel>
            <div class="description">
              応募者の引用のコメントに、以下のキーワードが含まれる場合、メッセージが配信されます。
            </div>
            <div class="input">
              <tag-input
                :tags="message.commentKeyword"
                add-tag-text="キーワード追加"
                :onChange="handleCommentKeywordsChange"
                tabindex="8"
                :disabled="isFinished || canNotEditInstantwin"
              ></tag-input>
            </div>
          </div>
        </Panel>
      </div>
      <!-- 重み付け値-->
      <div class="setting-content">
        <Panel>
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="重み付け値"></FormItemLabel>
              <el-tooltip placement="top">
                <div slot="content">
                  同じ配信期間で同じ種別のメッセージがあった場合、重み付け値が高いメッセージが優先されます。<br />
                  同じ重み付け値の場合、ランダムで1つのメッセージを処理します。
                </div>
                <i class="mdi mdi-help-circle"></i>
              </el-tooltip>
            </div>
            <el-input-number
              controls-position="right"
              :min="0"
              :max="99"
              v-model="message.weightCount"
              @change="handleInputNumberChange($event, 'weightCount')"
              tabindex="9"
              :disabled="isFinished || canNotEditInstantwin"
            ></el-input-number>
          </div>
        </Panel>
      </div>
      <!-- 当選条件設定-->
      <div class="caption mb-x2" v-if="message.messageType === 'win'">{{ '当選条件設定' }}</div>
      <div class="setting-content" v-if="message.messageType === 'win'">
        <Panel>
          <!-- 当選数-->
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="当選数"></FormItemLabel>
              <el-tooltip placement="top">
                <div slot="content">メッセージの当選数を指定してください。当選数分の当選メッセージが配信されます。</div>
                <i class="mdi mdi-help-circle"></i>
              </el-tooltip>
            </div>
            <el-input-number
              controls-position="right"
              :min="0"
              :max="99999"
              tabindex="10"
              v-model="message.winnerCount"
              @change="handleInputNumberChange($event, 'winnerCount')"
              :disabled="isFinished || canNotEditInstantwin"
            ></el-input-number>
          </div>
          <!-- 1日の最大当選数-->
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="1日の最大当選数(0 = 制限なし)"></FormItemLabel>
              <el-tooltip placement="top">
                <div slot="content">1日の最大当選数を設定できます。(最終日を除く)</div>
                <i class="mdi mdi-help-circle"></i>
              </el-tooltip>
            </div>
            <el-input-number
              controls-position="right"
              :min="0"
              :max="99999"
              v-model="message.dailyWinnerCount"
              maxlength="5"
              tabindex="11"
              @change="handleInputNumberChange($event, 'dailyWinnerCount')"
              :disabled="isFinished || canNotEditInstantwin"
              :step="1"
            ></el-input-number>
          </div>
          <!-- 当選率変動-->
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="当選率変動"></FormItemLabel>
            </div>
            <div class="input">
              <el-radio
                border
                label="none"
                v-model="message.winningRateChangeType"
                :disabled="isFinished || canNotEditInstantwin"
                >変動なし</el-radio
              >
              <el-tooltip
                placement="top"
                content="当選数が全てはけるように、残り時間と残り当選数に応じて当選率を調整します。"
              >
                <el-radio
                  border
                  label="auto"
                  v-model="message.winningRateChangeType"
                  :disabled="isFinished || canNotEditInstantwin"
                  >自動変動</el-radio
                >
              </el-tooltip>
              <el-radio
                border
                label="time"
                v-model="message.winningRateChangeType"
                :disabled="isFinished || canNotEditInstantwin"
                >指定期間変動</el-radio
              >
            </div>
          </div>
          <!-- 当選率-->
          <div class="input-container" v-if="message.winningRateChangeType !== 'auto'">
            <div class="title">
              <FormItemLabel label="当選率(%)"></FormItemLabel>
              <el-tooltip placement="top">
                <div slot="content">応募の当選確率を入力してください。</div>
                <i class="mdi mdi-help-circle"></i>
              </el-tooltip>
            </div>
            <el-input-number
              controls-position="right"
              :min="0"
              :max="100"
              v-model="message.winningRate"
              maxlength="3"
              tabindex="12"
              @change="handleInputNumberChange($event, 'winningRate')"
              :disabled="isFinished || canNotEditInstantwin"
              :step="1"
            ></el-input-number>
          </div>
          <!-- 当選率変動の指定期間変動-->
          <div class="input-container" v-if="message.winningRateChangeType === 'time'">
            <span class="mr-x2 label-before">配信期間の</span>
            <el-input-number
              controls-position="right"
              :min="1"
              :max="9"
              v-model="message.elapsedRate"
              @change="handleInputNumberChange($event, 'elapsedRate')"
              maxlength="3"
              tabindex="13"
              :disabled="isFinished || canNotEditInstantwin"
            ></el-input-number
            ><span class="ml-x2">割経過時</span>
          </div>
          <div class="input-container" v-if="message.winningRateChangeType === 'time'">
            <span class="mr-x2 label-before">当選率を</span>
            <el-input-number
              controls-position="right"
              :min="0"
              :max="100"
              :step="1"
              v-model="message.elapsedWinningRate"
              @change="handleInputNumberChange($event, 'elapsedWinningRate')"
              tabindex="14"
              :disabled="isFinished || canNotEditInstantwin"
            ></el-input-number
            ><span class="ml-x2">%にする</span>
          </div>
        </Panel>
      </div>
      <div class="setting-content" v-if="message.messageType === 'win'">
        <Panel>
          <!-- 1日1回抽選-->
          <div class="input-container">
            <el-switch
              v-model="message.isDailyLottery"
              :disabled="isFinished || canNotEditInstantwin"
              active-text="1日1回抽選"
              :active-value="1"
              :inactive-value="0"
            ></el-switch>
            <el-tooltip
              placement="top"
              content="ONにすると、1日の切り替え時間を経過後、応募者の過去の応募履歴に関わらず再度抽選します。"
              ><i class="mdi mdi-help-circle"></i
            ></el-tooltip>
          </div>
          <!-- 当メッセージで当選実績がある場合、落選にする-->
          <div class="input-container">
            <el-switch
              v-model="message.excludeMessageMultiWinner"
              :disabled="excludeMessageMultiWinnerDisabled"
              active-text="当メッセージで当選実績がある場合、落選にする"
              :active-value="1"
              :inactive-value="0"
            ></el-switch>
            <el-tooltip placement="top">
              <div slot="content">
                <span v-if="campaign.setting.excludeMultiWinner"
                  >OFFに変更したい場合、キャンペーン設定の当キャンペーンで当選実績がある場合、落選にするフラグをOFFにしてください。</span
                ><span v-else
                  >当メッセージですでに当選している場合、落選となります。<br />OFFの場合、重複当選を許可します。</span
                >
              </div>
              <i class="mdi mdi-help-circle"></i>
            </el-tooltip>
          </div>
          <!-- 別メッセージで当選実績がある場合、落選にする-->
          <div class="input-container">
            <el-switch
              v-model="message.excludeOtherMessageMultiWinner"
              :disabled="excludeOtherMessageMultiWinnerDisabled"
              active-text="別メッセージで当選実績がある場合、落選にする"
              :active-value="1"
              :inactive-value="0"
            ></el-switch>
            <el-tooltip placement="top" :content="excludeOtherMessageMultiWinnerTooltip"
              ><i class="mdi mdi-help-circle"></i
            ></el-tooltip>
          </div>
        </Panel>
      </div>
      <!-- メッセージ設定-->
      <div class="caption mb-x2">メッセージ設定</div>
      <div class="setting-content">
        <Panel>
          <!-- 配信方法-->
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="配信方法"></FormItemLabel>
              <el-tooltip placement="top">
                <div slot="content">メッセージの配信方法を選択してください。当選の場合のみDMを配信できます。</div>
                <i class="mdi mdi-help-circle"></i>
              </el-tooltip>
            </div>
            <div class="input">
              <el-radio
                border
                label="mention"
                v-model="message.replyType"
                tabindex="15"
                :disabled="isFinished || canNotEditInstantwin"
                >メンション</el-radio
              >
              <el-radio
                border
                label="dm"
                v-model="message.replyType"
                v-if="message.messageType === 'win'"
                tabindex="16"
                :disabled="isFinished || canNotEditInstantwin"
                >DM</el-radio
              >
              <el-radio
                border
                label="mention-and-dm"
                tabindex="17"
                v-model="message.replyType"
                v-if="message.messageType === 'win'"
                :disabled="isFinished || canNotEditInstantwin"
                >メンション & DM</el-radio
              >
            </div>
          </div>
          <!-- メンションを公開する-->
          <div class="input-container" v-if="hasMentionBody && hasAdsAccountId">
            <el-switch
              v-model="message.isPublicReply"
              active-text="メンションを公開する"
              :active-value="1"
              :inactive-value="0"
              :disabled="isFinished || canNotEditInstantwin"
            ></el-switch>
            <el-tooltip
              placement="top"
              content="ONにすると Xプロフィールの [ポストと返信] や [メディア] タブにメンションが公開表示されます。"
              ><i class="mdi mdi-help-circle"></i
            ></el-tooltip>
          </div>
        </Panel>
      </div>
      <div class="setting-content">
        <Panel>
          <!-- テンプレート-->
          <div class="message message-warning mb-x2" v-if="hasMentionBody && !hasAdsAccountId">
            広告アカウントが見つからないため、メンションを設定できません。
          </div>
          <div class="input-container" v-if="hasMentionBody && hasAdsAccountId">
            <div class="title">
              <FormItemLabel label="メンション設定"></FormItemLabel>
            </div>
            <div class="label">テンプレート</div>
            <div class="template-manage">
              <el-select
                class="mr-x2"
                v-model="mentionTemplate"
                @change="onChangeMentionTemplate"
                :disabled="isFinished || canNotEditInstantwin"
                placeholder="テンプレートを呼び出す場合、選択してください"
              >
                <el-option
                  v-for="(template, index) in messageTemplates"
                  :value="template.id"
                  :label="template.name"
                  :key="index"
                ></el-option> </el-select
              ><a href="/settings/group#templates" target="_blank"
                ><i class="mdi mdi-open-in-new"></i>テンプレート管理</a
              >
            </div>
          </div>
          <!-- メンション内容-->
          <div class="input-container" v-if="hasMentionBody && hasAdsAccountId">
            <div class="label">
              メンション内容
              <el-tooltip placement="top">
                <div slot="content">メンションの内容をここに入力してください。1つ以上URLを含める必要があります。</div>
                <i class="mdi mdi-help-circle"></i>
              </el-tooltip>
              <div class="sub">{{ formatNumber(mentionBodyCount) }}/{{ formatNumber(maxMentionLength) }}</div>
            </div>
            <el-input
              type="textarea"
              placeholder="内容入力"
              :rows="8"
              resize="vertical"
              v-model="message.mentionBody"
              tabindex="18"
              :disabled="isFinished || canNotEditInstantwin"
            ></el-input>
            <div class="input-invalid-text" v-if="mentionBodyCount > maxMentionLength">
              文字数は{{ maxMentionLength }}文字以下にしてください。
            </div>
            <div class="input-invalid-text" v-if="errors.mentionNotContainLink && dailySendLimitCount >= 2">
              同一内容ポストエラー防止のため、内容に必ずURLを含めるようにしてください。
            </div>
            <div class="input-invalid-text" v-if="mentionBodyCount === 0">メンション内容を入力してください。</div>
          </div>
          <!-- メンションの動画を追加-->
          <div
            class="input-container upload-block"
            v-if="hasMentionBody && hasAdsAccountId"
            v-loading="uploadingMentionMedia"
            :element-loading-text="mentionLoadingText"
          >
            <div class="label">メディア / カードの追加</div>
            <div class="input">
              <el-radio
                border
                label="none"
                v-model="message.mentionMediaType"
                tabindex="19"
                :disabled="isFinished || canNotEditInstantwin"
                >なし</el-radio
              >
              <el-radio
                border
                label="image_video"
                v-model="message.mentionMediaType"
                tabindex="20"
                :disabled="isFinished || canNotEditInstantwin"
                >画像または動画</el-radio
              >
              <el-radio
                border
                label="card"
                tabindex="21"
                v-model="message.mentionMediaType"
                :disabled="isFinished || canNotEditInstantwin || !hasAdsAccountId"
                >カード</el-radio
              >
            </div>
            <div class="mt-x1 message-media-card" v-if="isMentionImageVideo">
              <div class="label mb-x2">メディアの追加</div>
              <div class="mb-x2 media-preview" v-if="message.mentionMedia && message.mentionMedia.mediaOriginName">
                <i
                  class="mdi mdi-close"
                  @click="removeMentionMedia"
                  v-if="!canNotEditInstantwin && (isPreStart || isStarting)"
                ></i
                ><i
                  class="mdi-media mdi mdi-image"
                  v-if="message.mentionMedia && isImage(message.mentionMedia.mediaUrl)"
                ></i
                ><i
                  class="mdi-media mdi mdi-video"
                  v-if="message.mentionMedia && !isImage(message.mentionMedia.mediaUrl)"
                ></i>
                <div class="media-name">{{ message.mentionMedia.mediaOriginName }}</div>
                <img
                  class="instantwin-media mt-x1"
                  v-if="message.mentionMedia && isImage(message.mentionMedia.mediaUrl)"
                  :src="message.mentionMedia.mediaUrl"
                />
                <video
                  class="instantwin-media mt-x2"
                  v-if="message.mentionMedia && !isImage(message.mentionMedia.mediaUrl)"
                  :src="message.mentionMedia.mediaUrl"
                  controls="true"
                ></video>
              </div>
              <div>
                <label class="el-button el-button--default" v-if="!canNotEditInstantwin && (isPreStart || isStarting)"
                  ><i class="mdi mdi-image"></i><span v-if="!message.mentionMedia">画像/動画を追加</span
                  ><span v-if="message.mentionMedia">画像/動画を変更</span>
                  <input
                    type="file"
                    style="display: none"
                    @change="handleMentionFileChange($event)"
                    accept="video/mp4,image/jpeg,image/png,image/pjpeg,image/gif"
                    ref="mentionMediaInput"
                  /> </label
                ><span class="pl-x2" v-if="isPreStart || isStarting">アップロード可能最大サイズ: 画像5MB 動画25MB</span>
              </div>
            </div>
            <div class="message-media-card mt-x1" v-if="isMentionCard">
              <div v-if="!isPreviewMentionCard">
                <div class="label mb-x2">
                  カードの選択
                  <el-tooltip placement="top" content="X広告で作成済のカードのみが選択できます。"
                    ><i class="mdi mdi-help-circle"></i
                  ></el-tooltip>
                </div>
                <div layout layout-align="space-between-center">
                  <div class="card-type mr-x2" layout="column">
                    <el-input
                      :placeholder="'カード名検索...'"
                      v-model="mentionCardOptions.keyword"
                      @input="getCardList('input')"
                      ><i class="mdi mdi-magnify" slot="prepend"></i
                    ></el-input>
                  </div>
                  <div class="card-type mr-x2" layout="column">
                    <el-select v-model="mentionCardOptions.card_type" @change="getCardList('select')">
                      <el-option
                        v-for="card of cardTypeList"
                        :key="card.value"
                        :label="card.label"
                        :value="card.value"
                      ></el-option>
                    </el-select>
                  </div>
                  <div class="media-type" layout="column">
                    <el-select v-model="mentionCardOptions.media_type" @change="getCardList('select')">
                      <el-option
                        v-for="media of mediaTypeList"
                        :key="media.value"
                        :label="media.label"
                        :value="media.value"
                      ></el-option>
                    </el-select>
                  </div>
                </div>
                <div class="mt-x2" v-if="hasMessageCards">
                  <div class="mention-card" v-for="(card, index) of mentionCards" :key="index">
                    <el-radio
                      class="mention-card-item"
                      border
                      :label="card.card_id"
                      v-model="mentionCardId"
                      @change="handleCardChange"
                      >{{ card.card_name }}
                      <div class="mt-x1">
                        <div class="card-media">
                          <video
                            class="card-media-image"
                            v-if="isUrlVideo(card.media_url)"
                            :src="card.media_url"
                          ></video
                          ><img class="card-media-image" v-else :src="card.media_url" /><i
                            class="card-media-icon mdi mdi-image-multiple"
                            v-if="card.media_type === 'carousel'"
                          ></i
                          ><i class="card-media-icon mdi mdi-image" v-if="card.media_type === 'image'"></i
                          ><i class="card-media-icon mdi mdi-video" v-if="card.media_type === 'video'"></i>
                        </div>
                        <div class="card-type-name mt-x1">{{ cardTypeLabel(card.card_type) }}</div>
                        <div class="card-created">{{ cardCreated(card.created) }}</div>
                      </div>
                    </el-radio>
                  </div>
                </div>
                <div class="card-button mt-x2" v-if="hasNextCursor">
                  <el-button @click="getCardList('more')">さらに読み込む</el-button>
                </div>
              </div>
              <div v-if="isPreviewMentionCard" layout layout-align="space-between-center">
                <div class="card-media-preview" layout>
                  <div class="card-media mr-x1">
                    <video
                      class="card-media-image"
                      v-if="isUrlVideo(message.mentionCard.mediaUrl)"
                      :src="message.mentionCard.mediaUrl"
                    ></video
                    ><img class="card-media-image" v-else :src="message.mentionCard.mediaUrl" /><i
                      class="card-media-icon mdi mdi-image-multiple"
                      v-if="message.mentionCard.mediaType === 'carousel'"
                    ></i
                    ><i class="card-media-icon mdi mdi-image" v-if="message.mentionCard.mediaType === 'image'"></i
                    ><i class="card-media-icon mdi mdi-video" v-if="message.mentionCard.mediaType === 'video'"></i>
                  </div>
                  <div class="card-media-info">
                    <div class="card-name">{{ message.mentionCard.cardName }}</div>
                    <div class="card-type-name">{{ cardTypeLabel(message.mentionCard.cardType) }}</div>
                    <div class="card-created">{{ cardCreated(message.mentionCard.createdAt) }}</div>
                  </div>
                </div>
                <el-button @click="removeMentionCard" :disabled="isFinished || canNotEditInstantwin"
                  ><i class="mdi mdi-delete-forever"></i>解除</el-button
                >
              </div>
            </div>
          </div>
          <!-- テンプレート-->
          <div class="input-container" v-if="hasMessageBody">
            <div class="title">
              <FormItemLabel label="DM設定"></FormItemLabel>
            </div>
            <div class="label">テンプレート</div>
            <div class="template-manage">
              <el-select
                class="mr-x2"
                v-model="messageTemplate"
                @change="onChangeMessageTemplate"
                :disabled="isFinished || canNotEditInstantwin"
                placeholder="テンプレートを呼び出す場合、選択してください"
              >
                <el-option
                  v-for="(template, index) in messageTemplates"
                  :value="template.id"
                  :label="template.name"
                  :key="index"
                ></el-option> </el-select
              ><a href="/settings/group#templates" target="_blank"
                ><i class="mdi mdi-open-in-new"></i>テンプレート管理</a
              >
            </div>
          </div>
          <!-- ダイレクトメッセージ内容-->
          <div class="input-container" v-if="hasMessageBody">
            <div class="label">
              ダイレクトメッセージ内容
              <el-tooltip placement="top">
                <div slot="content">ダイレクトメッセージの内容をここに入力してください。</div>
                <i class="mdi mdi-help-circle"></i>
              </el-tooltip>
              <div class="sub">{{ formatNumber(messageBodyCount) }}/4,000</div>
            </div>
            <editable
              class="body-texarea"
              v-model="message.messageBody"
              tabindex="22"
              :placeholder="'内容入力'"
              :disabled="isFinished || canNotEditInstantwin"
              :highlights="highlights"
            ></editable>
            <div class="input-invalid-text" v-if="messageBodyCount > maxMessageLength">
              文字数は4,000文字以下にしてください。
            </div>
            <div class="input-invalid-text" v-if="messageBodyCount === 0">メッセージ内容を入力してください。</div>
            <div class="mt-x1" v-if="!message.isSerialCodeConfigured && hasSerialCodeInMessageBody">
              <Message type="danger"
                >シリアルコードが未登録のため、このままではメッセージを配信できません。<br />配信開始までに
                [シリアルコードの管理] から追加してください。</Message
              >
            </div>
            <div class="mt-x1" v-if="message.isSerialCodeConfigured && !hasSerialCodeInMessageBody">
              <Message type="warning"
                >登録されているシリアルコードがあります。<br />配信する場合は、変数(_serial_code_)をメッセージ内容に入力してください。</Message
              >
            </div>
          </div>
          <!-- ダイレクトメッセージ内容の画像/動画を追加-->
          <div
            class="input-container upload-block"
            v-if="hasMessageBody"
            v-loading="uploadingMessageMedia"
            :element-loading-text="messageLoadingText"
          >
            <div class="label">メディアの追加</div>
            <div class="input">
              <el-radio
                border
                label="none"
                v-model="message.messageMediaType"
                tabindex="23"
                :disabled="isFinished || canNotEditInstantwin"
                >なし</el-radio
              >
              <el-radio
                border
                label="image_video"
                v-model="message.messageMediaType"
                tabindex="24"
                :disabled="isFinished || canNotEditInstantwin"
                >画像または動画</el-radio
              >
            </div>
            <div class="mt-x1 message-media-card" v-if="isMessageImageVideo">
              <div class="mb-x2 media-preview" v-if="message.messageMedia && message.messageMedia.mediaOriginName">
                <i
                  class="mdi mdi-close"
                  @click="removeMessageMedia"
                  v-if="!canNotEditInstantwin && (isPreStart || isStarting)"
                ></i
                ><i
                  class="mdi-media mdi mdi-image"
                  v-if="message.messageMedia && isImage(message.messageMedia.mediaUrl)"
                ></i
                ><i
                  class="mdi-media mdi mdi-video"
                  v-if="message.messageMedia && !isImage(message.messageMedia.mediaUrl)"
                ></i>
                <div class="media-name">{{ message.messageMedia.mediaOriginName }}</div>
                <img
                  class="instantwin-media mt-x1"
                  v-if="message.messageMedia && isImage(message.messageMedia.mediaUrl)"
                  :src="message.messageMedia.mediaUrl"
                />
                <video
                  class="instantwin-media mt-x2"
                  v-if="message.messageMedia && !isImage(message.messageMedia.mediaUrl)"
                  :src="message.messageMedia.mediaUrl"
                  controls="true"
                ></video>
              </div>
              <div>
                <label class="el-button el-button--default" v-if="!canNotEditInstantwin && (isPreStart || isStarting)"
                  ><i class="mdi mdi-image"></i><span v-if="!message.messageMedia">画像/動画を追加</span
                  ><span v-if="message.messageMedia">画像/動画を変更</span>
                  <input
                    type="file"
                    style="display: none"
                    @change="handleMessageFileChange($event)"
                    accept="video/mp4,image/jpeg,image/png,image/pjpeg,image/gif"
                    ref="messageMediaInput"
                  /> </label
                ><span class="pl-x2" v-if="isPreStart || isStarting">アップロード可能最大サイズ: 画像5MB 動画25MB</span>
              </div>
            </div>
          </div>
          <div class="input-container mt-x4" v-if="hasMessageBody">
            <MessageConvert :is-instantwin="true"></MessageConvert>
          </div>
        </Panel>
      </div>
    </div>
    <div class="dialog-footer" slot="footer">
      <el-button
        v-if="mode === 'add'"
        type="primary"
        @click="createMessage()"
        :disabled="!formValid || $v.$invalid || this.uploadingMessageMedia || this.uploadingMentionMedia"
        ><i class="mdi mdi-content-save"></i>新規作成</el-button
      >
      <div v-if="mode === 'edit'" layout layout-align="space-between">
        <el-button
          class="pull-left"
          type="default"
          @click="deleteMessage()"
          :disabled="isFinished || canNotEditInstantwin"
          ><i class="mdi mdi-delete-forever"></i>削除</el-button
        >
        <el-button
          type="primary"
          @click="updateMessage()"
          :disabled="
            $v.$invalid === true ||
            formValid === false ||
            isFinished ||
            canNotEditInstantwin ||
            this.uploadingMessageMedia ||
            this.uploadingMentionMedia
          "
          ><i class="mdi mdi-content-save"></i>保存</el-button
        >
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.input-container {
  width: 100%;

  &:not(:last-child) {
    margin-bottom: $spacing-unit * 2;
  }

  .label {
    position: relative;
    margin-bottom: $spacing-unit;

    .sub {
      position: absolute;
      top: 0;
      right: 0;
    }
  }

  .mdi-help-circle {
    margin-left: $spacing-unit;
    font-size: 20px;
  }

  .el-input-number {
    width: $spacing-unit * 18;
  }

  .label-before {
    display: inline-block;
    width: 70px;
  }

  .template-manage {
    div {
      display: inline-block;
    }

    .el-select {
      width: 60%;
    }
  }
}

.title {
  display: flex;
  margin-bottom: $spacing-unit;
}

.setting-content:not(:last-child) {
  margin-bottom: $spacing-unit * 2;
}

.instantwin-media {
  max-width: 100%;
  max-height: $spacing-unit * 32;
}

.body-texarea {
  min-height: 180px;
  max-height: 800px;
}

.description {
  margin-bottom: $spacing-unit;
  font-size: $font-small-size;
  color: $sub-text-color;
}

.media-preview {
  position: relative;
  display: inline-block;
  padding: math.div($spacing-unit, 2) $spacing-unit;
  border: 1px solid $border-color;
  border-radius: $round;

  .media-name {
    padding-right: $spacing-unit * 3;
  }

  .mdi {
    position: absolute;
    top: math.div($spacing-unit, 2);
    right: math.div($spacing-unit, 2);
    cursor: pointer;
  }
}

.message-media-card {
  padding: $spacing-unit * 2;
  border: 1px solid $border-color;
  border-radius: $round;
}

.mdi-media {
  top: unset !important;
  right: unset !important;
  bottom: $spacing-unit * 2;
  left: $spacing-unit * 2;
  color: $text-color-white;
}

.card-type {
  width: 250px;
}

.media-type {
  width: 110px;
}

.mention-card {
  display: inline-block;
  width: 25%;
  padding: $spacing-unit math.div($spacing-unit, 2);

  .mention-card-item {
    width: 100%;
    height: auto;
    padding: $spacing-unit;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    :deep(.el-radio__label) {
      font-size: $font-small-size;
    }
  }
}

.card-media {
  position: relative;
  width: 100%;
  height: $spacing-unit * 10;

  .card-media-image {
    width: 100%;
    height: 100%;
  }

  .card-media-icon {
    position: absolute;
    bottom: $spacing-unit;
    left: $spacing-unit;
    color: $text-color-white;
  }
}

.card-type-name {
  font-size: 10px;
  line-height: $line-height;
  white-space: normal;
}

.card-created {
  font-size: 10px;
  color: $sub-text-color;
}

.card-button {
  text-align: center;
}

.card-media-preview {
  .card-media {
    width: 130px;
  }

  .card-media-info {
    align-self: center;

    .card-name {
      font-weight: bold;
    }

    .card-type-name,
    .card-created {
      font-size: $font-small-size;
    }
  }
}
</style>
<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import moment from 'moment'
import { mapActions, mapState } from 'pinia'
import twitterText from 'twitter-text'
import { defineComponent } from 'vue'
import { required } from 'vuelidate/lib/validators'

import Editable from '@/client/components/_atoms/editable'
import Message from '@/client/components/_atoms/Message.vue'
import MessageConvert from '@/client/components/_atoms/message_convert'
import Panel from '@/client/components/_atoms/Panel.vue'
import TagInput from '@/client/components/_atoms/tag_input'
import FormItemLabel from '@/client/components/_molecules/FormItemLabel.vue'
import { CAMPAIGN_STATUS, CARD_TYPE, MEDIA_TYPE } from '@/client/constant'
import api from '@/client/core/api'
import TwitterUpload from '@/client/core/twitter_upload'
import { formatNumber } from '@/client/filters/filters'
import { useSystemStore } from '@/client/store/useSystemStore'
import { useTwitterInstantwinMessageStore } from '@/client/store/useTwitterInstantwinMessageStore'
import { containsWhitespace } from '@/client/utils/validation'

export default defineComponent({
  components: {
    TagInput,
    Editable,
    MessageConvert,
    Message,
    Panel,
    FormItemLabel
  },
  props: {
    mode: String,
    messageId: Number,
    messageType: String,
    visible: Boolean,
    isChangeWithoutSubmit: Boolean
  },
  data() {
    return {
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      uploadingMentionMedia: false,
      uploadingMessageMedia: false,
      uploadMessageTimer: null,
      uploadMentionTimer: null,
      isSubmit: false,
      maxMentionLength: 263,
      maxMessageLength: 4000,
      message: {
        id: '',
        name: '',
        messageType: '',
        winnerCount: 1,
        winningRate: 0.01,
        winningRateChangeType: 'none',
        dailyWinnerCount: 0,
        dailyWinnerChangeTime: '00:00',
        elapsedRate: 1,
        elapsedWinningRate: 1,
        weightCount: 1,
        entryKeyword: [],
        commentKeyword: [],
        replyType: 'mention',
        mentionBody: '',
        messageBody: '',
        mentionMedia: null,
        messageMedia: null,
        mentionCard: null,
        isDailyLottery: 0,
        excludeMessageMultiWinner: 1,
        excludeOtherMessageMultiWinner: 1,
        isPublicReply: 0,
        mentionMediaType: 'none',
        messageMediaType: 'none',
        isSerialCodeConfigured: false
      },
      errors: {
        notInputStartDate: false,
        lessThanCurrentTime: false,
        lessThanStartTime: false,
        notInputEndDate: false,
        mentionNotContainLink: false
      },
      datePickerOption: {
        disabledDate(time: any) {
          return moment(time.getTime()).format('YYYY-MM-DD') < moment().format('YYYY-MM-DD')
        }
      },
      timePickerOption: {
        start: '00:00',
        step: '01:00',
        end: '23:00'
      },
      mentionCardOptions: {
        campaign_id: null,
        keyword: '',
        card_type: 'all',
        media_type: 'image',
        next_cursor: ''
      },
      messageTemplates: [],
      oldMentionTemplate: '',
      mentionTemplate: '',
      oldMessageTemplate: '',
      messageTemplate: '',
      highlights: [{ type: 'info', keywords: ['_account_name_', '_screen_name_', '_account_id_', '_serial_code_'] }],
      isMessageValidating: false,
      isMentionValidating: false,
      rawData: {},
      timeout: null,
      mentionCardId: '',
      mentionCards: []
    }
  },
  computed: {
    ...mapState(useTwitterInstantwinMessageStore, {
      campaign: state => {
        if (state.campaign) {
          return state.campaign
        }

        return {}
      }
    }),

    ...mapState(useSystemStore, {
      currentGroup: state => state.currentGroup,
      allowCreateInstantwin: state => Boolean(state.currentGroup.allowCreateInstantwin),
      isSystemAdmin: state => Boolean(state.user.isSystemAdmin)
    }),

    cardTypeList() {
      return CARD_TYPE
    },

    mediaTypeList() {
      return MEDIA_TYPE
    },

    cardType() {
      const card = this.cardTypeList.find(card => card.value === this.mentionCardOptions.card_type)

      return card ? card.label : ''
    },

    excludeMessageMultiWinnerDisabled() {
      return (this.message.excludeMessageMultiWinner && !!this.campaign.setting.excludeMultiWinner) || this.isFinished
    },

    excludeOtherMessageMultiWinnerTooltip() {
      if (this.campaign.setting.excludeMultiWinner) {
        return 'OFFに変更したい場合、キャンペーン設定の当キャンペーンで当選実績がある場合、落選にするフラグをOFFにしてください。'
      }
      return '当キャンペーン内に別メッセージですでに当選している場合、落選となります。'
    },

    excludeOtherMessageMultiWinnerDisabled() {
      return (
        (this.message.excludeOtherMessageMultiWinner && !!this.campaign.setting.excludeMultiWinner) || this.isFinished
      )
    },

    mentionBodyCount() {
      return twitterText.parseTweet(this.message.mentionBody).weightedLength
    },

    dailySendLimitCount() {
      return this.campaign.setting.dailySendLimitCount
    },

    messageBodyCount() {
      return twitterText.parseTweet(this.message.messageBody).weightedLength
    },

    hasSerialCodeInMessageBody() {
      return /_serial_code_/.test(this.message.messageBody)
    },

    hasMentionBody() {
      return (
        (this.message.replyType === 'mention' || this.message.replyType === 'mention-and-dm') &&
        (this.message.messageType === 'win' || this.message.messageType === 'lose')
      )
    },

    hasMessageBody() {
      return (
        (this.message.replyType === 'dm' || this.message.replyType === 'mention-and-dm') &&
        this.message.messageType === 'win'
      )
    },

    hasAdsAccountId() {
      if (this.campaign && this.campaign.account && this.campaign.account.adsAccountId) {
        return true
      }

      return false
    },

    hasMessageCards() {
      return Array.isArray(this.mentionCards)
    },

    hasNextCursor() {
      return !!this.mentionCardOptions.next_cursor
    },

    isPreStart() {
      if (this.campaign) {
        return this.campaign.status === CAMPAIGN_STATUS.BEFORE_EVENT
      }

      return false
    },

    isStarting() {
      if (this.campaign) {
        return [CAMPAIGN_STATUS.BEFORE_COLLECTION, CAMPAIGN_STATUS.DURING_COLLECTION].includes(this.campaign.status)
      }

      return false
    },

    isFinished() {
      if (this.campaign) {
        return [CAMPAIGN_STATUS.DURING_FINAL_COLLECTION, CAMPAIGN_STATUS.COLLECTION_COMPLETED].includes(
          this.campaign.status
        )
      }

      return false
    },

    isMessageImageVideo() {
      return this.message.messageMediaType === 'image_video'
    },

    isMentionImageVideo() {
      return this.message.mentionMediaType === 'image_video'
    },

    isMentionCard() {
      return this.message.mentionMediaType === 'card'
    },

    isPreviewMentionCard() {
      return this.message.mentionCard && !this.mentionCardId
    },

    formValid() {
      if (
        this.campaign &&
        [CAMPAIGN_STATUS.DURING_FINAL_COLLECTION, CAMPAIGN_STATUS.COLLECTION_COMPLETED].includes(this.campaign.status)
      ) {
        return true
      }

      if (!this.timeValid) {
        return false
      }

      this.$v.$touch()

      if (!this.message.messageType) {
        return false
      }

      if (
        (this.mentionBodyCount > this.maxMentionLength || !this.message.mentionBody.trim()) &&
        (this.message.replyType === 'mention' || this.message.replyType === 'mention-and-dm')
      ) {
        return false
      }

      if (
        (this.messageBodyCount > this.maxMessageLength || !this.message.messageBody.trim()) &&
        (this.message.replyType === 'dm' || this.message.replyType === 'mention-and-dm')
      ) {
        return false
      }

      for (const key in this.errors) {
        if (this.errors[key]) {
          if (key === 'mentionNotContainLink' && this.message.replyType === 'dm') {
            continue
          }

          if (
            key === 'mentionNotContainLink' &&
            (this.message.replyType === 'mention' || this.message.replyType === 'mention-and-dm') &&
            this.dailySendLimitCount < 2
          ) {
            continue
          }

          return false
        }
      }

      return true
    },

    timeValid() {
      const startTime = this.startTime || '00:00'
      const endTime = this.endTime || '00:00'
      const startDatetime = `${this.startDate} ${startTime}`
      const endDatetime = `${this.endDate} ${endTime}`

      if (this.startTime && !this.startDate) {
        this.errors.notInputStartDate = true
      } else {
        this.errors.notInputStartDate = false
      }

      if (this.startDate && this.campaign.status === CAMPAIGN_STATUS.BEFORE_EVENT) {
        this.errors.lessThanCurrentTime = startDatetime < moment().format('YYYY-MM-DD HH:mm')
        const campaignStartTime = moment(this.campaign.startDatetime)
        if (startDatetime < campaignStartTime.format('YYYY-MM-DD HH:mm') && !this.errors.lessThanCurrentTime) {
          this.startDate = campaignStartTime.format('YYYY-MM-DD')
          this.startTime = campaignStartTime.format('HH:mm')
        }
      } else {
        this.errors.lessThanCurrentTime = false
      }

      if (this.endTime && !this.endDate) {
        this.errors.notInputEndDate = true
      } else {
        this.errors.notInputEndDate = false
      }

      if (this.endDate && this.campaign.status !== 0) {
        if (this.startDate) {
          this.errors.lessThanStartTime = endDatetime < startDatetime
        } else {
          this.errors.lessThanStartTime = false
        }

        const campaignEndDateTime = moment(this.campaign.endDatetime)
        if (endDatetime > campaignEndDateTime.format('YYYY-MM-DD HH:mm') && !this.errors.lessThenStartTime) {
          this.endDate = campaignEndDateTime.format('YYYY-MM-DD')
          this.endTime = campaignEndDateTime.format('HH:mm')
        }
      } else {
        this.errors.lessThanStartTime = false
      }

      return (
        !this.errors.notInputStartDate &&
        !this.errors.lessThanCurrentTime &&
        !this.errors.notInputEndDate &&
        !this.errors.lessThanStartTime
      )
    },

    submitData() {
      const data: any = {
        message: {
          campaignId: this.campaign.id,
          name: this.message.name,
          messageType: this.messageType,
          weightCount: this.message.weightCount,
          entryKeyword:
            this.message.entryKeyword && this.message.entryKeyword.length
              ? JSON.stringify(this.message.entryKeyword)
              : null,
          commentKeyword:
            this.message.commentKeyword && this.message.commentKeyword.length
              ? JSON.stringify(this.message.commentKeyword)
              : null
        }
      }

      const messageContent: any = {
        replyType: this.message.replyType
      }

      if (this.message.replyType.includes('mention')) {
        messageContent.mentionMediaType = this.message.mentionMediaType
        messageContent.mentionBody = this.message.mentionBody

        if (this.message.mentionMediaType === 'image_video') {
          if (this.message.mentionMedia) {
            messageContent.mentionMedia = this.message.mentionMedia
          } else {
            messageContent.mentionMediaType = 'none'
          }
        }

        if (this.message.mentionMediaType === 'card') {
          if (this.message.mentionCard) {
            messageContent.mentionCard = { ...this.message.mentionCard }
          } else {
            messageContent.mentionMediaType = 'none'
          }
        }
      }

      if (this.message.replyType.includes('dm')) {
        messageContent.messageMediaType = this.message.messageMediaType
        messageContent.messageBody = this.message.messageBody

        if (this.message.messageMediaType === 'image_video') {
          if (this.message.messageMedia) {
            messageContent.messageMedia = this.message.messageMedia
          } else {
            messageContent.messageMediaType = 'none'
          }
        }
      }

      data.messageContent = messageContent

      if (this.startDate) {
        const startTime = this.startTime || '00:00'
        data.message.startDatetime = moment(`${this.startDate} ${startTime}`).format()
      } else {
        data.message.startDatetime = null
      }

      if (this.endDate) {
        const endTime = this.endTime || '00:00'
        data.message.endDatetime = moment(`${this.endDate} ${endTime}`).format()
      } else {
        data.message.endDatetime = null
      }

      if (this.message.messageType === 'win') {
        data.message.winnerCount = this.message.winnerCount
        data.message.winningRate = this.message.winningRate
        data.message.winningRateChangeType = this.message.winningRateChangeType
        data.message.dailyWinnerCount = this.message.dailyWinnerCount
        data.message.dailyWinnerChangeTime = `${this.message.dailyWinnerChangeTime}:00`

        data.message.elapsedRate = this.message.winningRateChangeType === 'time' ? this.message.elapsedRate : null
        data.message.elapsedWinningRate =
          this.message.winningRateChangeType === 'time' ? this.message.elapsedWinningRate : null
      } else {
        data.message.winnerCount = 0
        data.message.winningRate = 0
        data.message.winningRateChangeType = 'none'
        data.message.dailyWinnerCount = 0
        data.message.dailyWinnerChangeTime = ''

        data.message.elapsedRate = null
        data.message.elapsedWinningRate = null
      }

      if (this.message.replyType === 'dm') {
        data.message.isPublicReply = 0
      } else {
        data.message.isPublicReply = this.message.isPublicReply
      }

      if (!this.hasAdsAccountId) {
        data.message.isPublicReply = 1
      }

      data.message.isDailyLottery = this.message.isDailyLottery
      data.message.excludeMessageMultiWinner = this.message.excludeMessageMultiWinner
      data.message.excludeOtherMessageMultiWinner = this.message.excludeOtherMessageMultiWinner

      return data
    },

    messageLoadingText() {
      if (this.isMessageValidating) {
        return '画像・動画を検証しています。 しばらくお待ちください。'
      }

      return '画像・動画をアップロードしています。 しばらくお待ちください。'
    },

    mentionLoadingText() {
      if (this.isMentionValidating) {
        return '画像・動画を検証しています。 しばらくお待ちください。'
      }

      return '画像・動画をアップロードしています。 しばらくお待ちください。'
    },

    isFormChanged() {
      return JSON.stringify(this.submitData) !== JSON.stringify(this.rawData)
    },

    isIncludeQuoteTweets() {
      if (!this.campaign || !this.campaign.setting || !this.campaign.setting.entryActionType) {
        return false
      }

      return ['retweet_and_quote_tweet', 'quote_tweet'].includes(this.campaign.setting.entryActionType)
    },

    quoteKeywordText() {
      if (!this.campaign || !this.campaign.setting || !this.campaign.setting.entryActionType) {
        return ''
      }

      return this.campaign.setting.entryActionType === 'quote_tweet'
        ? '引用のコメントに以下のキーワードを含む'
        : '引用の場合、コメントに以下のキーワードを含む'
    },
    canNotEditInstantwin() {
      return !this.isSystemAdmin && !this.allowCreateInstantwin
    }
  },
  watch: {
    visible() {
      if (this.uploadMentionTimer) {
        clearInterval(this.uploadMentionTimer)
      }
      if (this.uploadMessageTimer) {
        clearInterval(this.uploadMessageTimer)
      }
    },

    'message.messageType'(value: string) {
      if (value === 'lose') {
        this.message.replyType = 'mention'
      }
    },

    'message.mentionBody'(value: string) {
      // URLを出力する。TwitterカードURLの場合は、除外する
      const urls: string[] = twitterText
        .extractUrls(value)
        .filter(url => url.indexOf('cards.twitter.com/cards/') === -1)

      if (!value.trim() || urls.length) {
        this.errors.mentionNotContainLink = false
      } else {
        this.errors.mentionNotContainLink = true
      }
    },

    isFormChanged(value: boolean) {
      this.$emit('update:isChangeWithoutSubmit', value)
    }
  },

  async created() {
    this.resetForm()

    if (this.messageId) {
      await this.getDataById(this.messageId)
    }

    await this.getMessageTemplates()

    this.rawData = this.submitData
    this.mentionCardOptions.campaign_id = this.campaign.id

    await this.getCardList()
  },
  methods: {
    formatNumber,

    ...mapActions(useTwitterInstantwinMessageStore, ['fetchInstantwinMessages']),

    /**
     * URLがビデオかどうかを判定する
     */
    isUrlVideo(mediaUrl: string) {
      if (!mediaUrl) return ''

      // media_typeだとcarouselの場合に一つ目のメディアが画像か動画かを判定できないため、URLから判定する
      const url = new URL(mediaUrl)

      return /\.mp4$/.test(url.pathname)
    },

    /**
     * カードを作成
     */
    cardCreated(date: string) {
      return moment(date).format('YYYY/MM/DD')
    },

    /**
     * カードタイプ表示
     */
    cardTypeLabel(type: string) {
      const cardType = CARD_TYPE.find(v => v.value === type)

      if (cardType) return cardType.label

      return ''
    },

    /**
     * カードの変更を処理
     */
    handleCardChange() {
      const card = this.mentionCards.find(card => card.card_id === this.mentionCardId)

      this.message.mentionCard = card
        ? {
            cardId: card.card_id,
            cardUri: card.card_uri,
            cardName: card.card_name,
            cardType: this.mentionCardOptions.card_type,
            mediaUrl: card.media_url,
            mediaType: card.media_type,
            createdAt: card.created
          }
        : null
    },

    async getMessageTemplates() {
      const params = {
        groupId: this.currentGroup.id,
        sortBy: 'name'
      }

      const messageTemplates = await api.get('/message_templates', {
        params
      })

      if (messageTemplates && messageTemplates.data && messageTemplates.data.templates) {
        this.messageTemplates = messageTemplates.data.templates
      }
    },

    /**
     * メッセージを作成
     */
    async createMessage() {
      if (this.isSubmit || !this.formValid) {
        return
      }

      this.isSubmit = true

      let uploadHasError = false
      // メンションメディアをアップロード
      if (this.message.mentionMedia && !this.message.mentionMedia.mediaId) {
        this.uploadingMentionMedia = true
        const data = {
          accountId: this.campaign.account.id,
          mediaUrl: this.message.mentionMedia.mediaUrl
        }

        uploadHasError = await this.uploadMedia(data, this.uploadMentionTimer, 'mention')
        this.uploadingMentionMedia = false
      }

      // メッセージメディアをアップロード
      if (this.message.messageMedia && !this.message.messageMedia.mediaId) {
        this.uploadingMessageMedia = true
        const data = {
          accountId: this.campaign.account.id,
          mediaUrl: this.message.messageMedia.mediaUrl
        }

        uploadHasError = await this.uploadMedia(data, this.uploadMessageTimer, 'message')
        this.uploadingMessageMedia = false
      }

      if (uploadHasError) {
        this.isSubmit = false
        return
      }

      const data = this.submitData
      const result = await api.post('/twitter_instantwin_messages', data)
      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      if (!result.data) {
        this.$notify({
          title: 'インスタントウィンメッセージの作成に失敗しました。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'インスタントウィンメッセージを作成しました。',
        customClass: 'success',
        duration: 5000
      })

      this.$emit('update:isChangeWithoutSubmit', false)

      // インスタントウィンメッセージ一覧を取得
      this.fetchInstantwinMessages()

      this.$emit('close')
    },

    /**
     * Execute upload media
     * @param {{ accountId: number, mediaUrl: string }} data
     * @param {any} timer
     * @param {string} typeMedia
     */
    async uploadMedia(data: { accountId: number; mediaUrl: string }, timer: any, typeMedia: string) {
      const media = await api.post('/twitter_uploads', data, {
        headers: {
          'Content-Type': undefined
        }
      })

      // アップロードプログレスを取得
      const progressResult = await this.getUploadProgress(media.data.filename, typeMedia)
      if (progressResult) {
        if (typeMedia === 'mention') {
          progressResult.mediaOriginName = this.message.mentionMedia.mediaOriginName
          this.message.mentionMedia = progressResult
        } else {
          progressResult.mediaOriginName = this.message.messageMedia.mediaOriginName
          this.message.messageMedia = progressResult
        }

        return false
      }

      return true
    },

    /**
     * Delete message
     */
    async deleteMessage() {
      const confirm = window.confirm('メッセージを削除してよろしいですか？')
      if (!confirm) {
        return
      }
      const result = await api.delete('/twitter_instantwin_messages/' + this.message.id)

      if (result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: '対象のメッセージはすでに削除されています。',
          customClass: 'danger',
          duration: 5000
        })
        this.$emit('close')
        // インスタントウィンメッセージ一覧を取得
        await this.fetchInstantwinMessages()
        return
      }

      // エラーがある場合はエラーメッセージを表示
      if (!result.data) {
        this.$notify({
          title: 'メッセージの削除に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'メッセージを削除しました。',
        customClass: 'success',
        duration: 5000
      })

      this.$emit('update:isChangeWithoutSubmit', false)

      // インスタントウィンメッセージ一覧を取得
      this.fetchInstantwinMessages()

      this.$emit('close')
    },

    /**
     * メッセージを更新
     */
    async updateMessage() {
      if (this.isSubmit || !this.formValid) {
        return
      }

      const data = this.submitData
      this.isSubmit = true
      const result = await api.put(`/twitter_instantwin_messages/${this.message.id}`, data)
      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      if (!result.data) {
        this.$notify({
          title: 'インスタントウィンメッセージの更新に失敗しました。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'インスタントウィンメッセージを更新しました。',
        customClass: 'success',
        duration: 5000
      })

      this.$emit('update:isChangeWithoutSubmit', false)

      // インスタントウィンメッセージ一覧を取得
      this.fetchInstantwinMessages()

      this.$emit('close')
    },

    /**
     * カードリストを取得
     */
    async getCardList(type?: string) {
      if (type === 'more') {
        if (!this.hasNextCursor) {
          return
        }

        const result = await this.getCardsFromApi()

        this.mentionCards = [...this.mentionCards, ...result.cards]
        this.mentionCardOptions.next_cursor = result.next_cursor

        return
      }

      if (type === 'input') {
        if (this.timeout) {
          clearTimeout(this.timeout)
        }

        this.timeout = setTimeout(async () => {
          this.mentionCardOptions.next_cursor = ''

          const result = await this.getCardsFromApi()

          this.mentionCards = result.cards
          this.mentionCardOptions.next_cursor = result.next_cursor
        }, 500)

        return
      }

      if (!type || type === 'select') {
        const result = await this.getCardsFromApi()

        this.mentionCards = result.cards
        this.mentionCardOptions.next_cursor = result.next_cursor
      }
    },

    async getCardsFromApi() {
      const result = await api.get('v1/twitter_instantwin_cards', {
        params: this.mentionCardOptions
      })

      return result.data
    },

    /**
     * データを埋める
     * @param {number} messageId
     */
    async getDataById(messageId: number) {
      const result = await api.get(`/twitter_instantwin_messages/${messageId}`)

      if (result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: '対象のメッセージはすでに削除されています。',
          customClass: 'danger',
          duration: 5000
        })
        this.$emit('close')
        // インスタントウィンメッセージ一覧を取得
        await this.fetchInstantwinMessages()
        return
      }

      const message = result.data

      this.message.id = message.id
      this.message.name = message.name
      if (message.startDatetime) {
        this.startDate = moment(message.startDatetime).format('YYYY-MM-DD')
        this.startTime = moment(message.startDatetime).format('HH:mm')
      }

      if (message.endDatetime) {
        this.endDate = moment(message.endDatetime).format('YYYY-MM-DD')
        this.endTime = moment(message.endDatetime).format('HH:mm')
      }

      this.message.messageType = message.messageType
      this.message.winnerCount = message.winnerCount
      this.message.winningRate = message.winningRate
      this.message.winningRateChangeType = message.winningRateChangeType
      this.message.dailyWinnerCount = message.dailyWinnerCount || 0
      this.message.dailyWinnerChangeTime = message.dailyWinnerChangeTime
        ? message.dailyWinnerChangeTime.substring(0, 5)
        : '00:00'

      this.message.elapsedRate = message.elapsedRate
      this.message.elapsedWinningRate = message.elapsedWinningRate
      this.message.weightCount = message.weightCount
      this.message.entryKeyword = message.entryKeyword ? JSON.parse(message.entryKeyword) : []
      this.message.commentKeyword = message.commentKeyword ? JSON.parse(message.commentKeyword) : []
      this.message.isDailyLottery = message.isDailyLottery
      this.message.excludeMessageMultiWinner = message.excludeMessageMultiWinner
      this.message.excludeOtherMessageMultiWinner = message.excludeOtherMessageMultiWinner
      this.message.isPublicReply = message.isPublicReply
      this.message.mediaType = message.tw_instantwin_message_contents[0].mediaType
      this.message.isSerialCodeConfigured = message.isSerialCodeConfigured

      switch (message.tw_instantwin_message_contents.length) {
        case 2:
          this.message.replyType = 'mention-and-dm'
          break
        case 1:
          this.message.replyType = message.tw_instantwin_message_contents[0].replyType
          break
        default:
          this.message.replyType = ''
          break
      }

      for (const content of message.tw_instantwin_message_contents) {
        if (content.replyType.includes('mention')) {
          this.message.mentionBody = content.body
          this.message.mentionMediaType = content.mediaType

          if (content.mediaType === 'image_video' && content.tw_instantwin_message_content_medias.length) {
            const media = content.tw_instantwin_message_content_medias[0]
            this.message.mentionMedia = media
              ? { mediaUrl: media.mediaUrl, mediaOriginName: media.mediaOriginName }
              : null
          }

          if (content.mediaType === 'card' && content.tw_instantwin_message_content_card) {
            this.message.mentionCard = content.tw_instantwin_message_content_card
          }
        }

        if (content.replyType.includes('dm')) {
          this.message.messageBody = content.body
          this.message.messageMediaType = content.mediaType

          if (content.mediaType === 'image_video' && content.tw_instantwin_message_content_medias.length) {
            const media = content.tw_instantwin_message_content_medias[0]
            this.message.messageMedia = media
              ? { mediaUrl: media.mediaUrl, mediaOriginName: media.mediaOriginName }
              : null
          }
        }
      }
    },

    /**
     * 入力番号の変更
     * @param {number | null} value
     * @param {string} inputName
     */
    handleInputNumberChange(value: number | null, inputName: string) {
      // 1日の最大当選数が当選数より多い場合はリセットする
      if (inputName === 'dailyWinnerCount') {
        if (value > this.message.winnerCount) {
          this.resetNumber(inputName)
        }
        return
      }

      if (!value) {
        this.resetNumber(inputName)
      }

      if (inputName === 'winnerCount' && value <= this.message.dailyWinnerCount) {
        this.resetNumber('dailyWinnerCount')
      }
    },

    /**
     * キーワードの変更
     * @param {string[]} keywords
     */
    handleKeywordsChange(keywords: string[]) {
      const origin = [...this.message.entryKeyword]
      this.message.entryKeyword = keywords

      if (keywords.join('').length > 50) {
        this.message.entryKeyword = [...origin]
        this.$notify({
          title: 'キーワードの合計文字数は、50文字以下にしてください。',
          customClass: 'danger',
          duration: 5000
        })
      }

      const keywordsText = keywords.join('')
      const symbol = /[!"$%&'()*+-.,/:;<=>?@[\\\]^`{|}~¥]/g

      if (keywordsText.match(symbol)) {
        this.message.entryKeyword = [...origin]
        this.$notify({
          title: 'キーワードに記号が含まれています。',
          customClass: 'danger',
          duration: 5000
        })
      }

      const isInvalid = containsWhitespace(keywords)

      if (isInvalid) {
        this.message.entryKeyword = [...origin]
        this.$notify({
          title: 'キーワードに利用できない文字列が含まれています。',
          customClass: 'danger',
          duration: 5000
        })
      }
    },

    /**
     * コメント内キーワードの変更
     */
    handleCommentKeywordsChange(commentKeywords: string[]) {
      const origin = [...this.message.commentKeyword]
      this.message.commentKeyword = commentKeywords

      if (commentKeywords.join('').length > 50) {
        this.message.commentKeyword = [...origin]
        this.$notify({
          title: 'キーワードの合計文字数は、50文字以下にしてください。',
          customClass: 'danger',
          duration: 5000
        })
      }

      const keywordsText = commentKeywords.join('')
      const symbol = /[!"$%&'()*+-.,/:;<=>?@[\\\]^`{|}~¥]/g

      if (keywordsText.match(symbol)) {
        this.message.commentKeyword = [...origin]
        this.$notify({
          title: 'キーワードに記号が含まれています。',
          customClass: 'danger',
          duration: 5000
        })
      }

      const isInvalid = containsWhitespace(commentKeywords)

      if (isInvalid) {
        this.message.commentKeyword = [...origin]
        this.$notify({
          title: 'キーワードに利用できない文字列が含まれています。',
          customClass: 'danger',
          duration: 5000
        })
      }
    },

    /**
     * メディアのエラーを表示する
     * @param {string} error
     * @param {string[]} validFormat
     */
    showError(error: string, validFormat: string[]) {
      let title = ''
      let message = ''

      switch (error) {
        case 'INVALID_MEDIA':
          title = 'Please upload valid format!'
          message = validFormat.join(', ')
          break
        case 'MAX_SIZE_VIDEO':
          title = '25MB以上のビデオを利用することはできません。 サイズを調整してからアップロードしてください。'
          break
        case 'MAX_SIZE_IMAGE':
          title = '5MB以上の画像を利用することはできません。サイズを調整してからアップロードしてください。'
          break
        case 'INVALID_DIMENSION':
          title = '画像・動画アップロードに失敗しました。'
          message =
            '詳しくは<a target="_blank" href="https://help-atelu.comnico.jp/available-image-and-video">こちら</a>をご確認ください。'
          break
      }

      this.$notify({
        title,
        message,
        customClass: 'danger',
        dangerouslyUseHTMLString: true,
        duration: 5000
      })
    },

    /**
     * ファイルのメンションを変更
     */
    async handleMentionFileChange(e) {
      if (!e.target.files || !e.target.files[0]) {
        return
      }

      const file = e.target.files[0]
      this.$refs.mentionMediaInput.value = null

      const validFormat = ['video/mp4', 'image/jpeg', 'image/png', 'image/gif', 'image/pjpeg', 'image/gif']

      // メディアをチェック
      const checkValidMedia = await TwitterUpload.checkValidMedia(file, validFormat)
      if (checkValidMedia.error) {
        this.showError(checkValidMedia.error, validFormat)
        return
      }

      this.uploadingMentionMedia = true

      const formData = new FormData()
      formData.append('file', file)

      this.isMentionValidating = true

      if (file.type.includes('video')) {
        const checkMedia = await TwitterUpload.checkMedia(formData)
        if (!checkMedia) {
          this.$notify({
            title: '画像・動画アップロードに失敗しました。',
            message:
              '詳しくは<a target="_blank" href="https://help-atelu.comnico.jp/available-image-and-video">こちら</a>をご確認ください。',
            customClass: 'danger',
            dangerouslyUseHTMLString: true,
            duration: 5000
          })
          this.uploadingMentionMedia = false
          return
        }
      }

      this.isMentionValidating = false

      formData.append('accountId', this.campaign.account.id)

      const result = await TwitterUpload.uploadMedia(formData)

      // アップロードプログレスを取得
      const progressResult = await this.getUploadProgress(result.data.filename, 'mention')
      if (progressResult) {
        this.message.mentionMedia = progressResult
        this.message.mentionMedia.mediaOriginName = file.name
      }

      this.uploadingMentionMedia = false
    },

    /**
     * メッセージファイルを変更
     */
    async handleMessageFileChange(e) {
      if (!e.target.files || !e.target.files[0]) {
        return
      }

      const file = e.target.files[0]
      this.$refs.messageMediaInput.value = null

      const validFormat = ['video/mp4', 'image/jpeg', 'image/png', 'image/gif', 'image/pjpeg', 'image/gif']

      // メディアをチェック
      const checkValidMedia = await TwitterUpload.checkValidMedia(file, validFormat)
      if (checkValidMedia.error) {
        this.showError(checkValidMedia.error, validFormat)
        return
      }

      const formData = new FormData()
      formData.append('file', file)

      this.uploadingMessageMedia = true

      if (file.type.includes('video')) {
        const checkMedia = await TwitterUpload.checkMedia(formData)
        if (!checkMedia) {
          this.$notify({
            title: '画像・動画アップロードに失敗しました。',
            message:
              '詳しくは<a target="_blank" href="https://help-atelu.comnico.jp/available-image-and-video">こちら</a>をご確認ください。',
            customClass: 'danger',
            dangerouslyUseHTMLString: true,
            duration: 5000
          })
          this.uploadingMessageMedia = false
          return
        }
      }

      this.isMessageValidating = true

      this.isMessageValidating = false

      formData.append('accountId', this.campaign.account.id)
      formData.append('forDM', 'true')

      const result = await TwitterUpload.uploadMedia(formData)

      // アップロードプログレスを取得
      const progressResult = await this.getUploadProgress(result.data.filename, 'message')
      if (progressResult) {
        this.message.messageMedia = progressResult
        this.message.messageMedia.mediaOriginName = file.name
      }

      this.uploadingMessageMedia = false
    },

    /**
     * change message template
     * @param {number} templateId
     */
    onChangeMessageTemplate(templateId: number) {
      const currentTemplate = this.messageTemplates.find(template => template.id === templateId)
      const body = currentTemplate.body

      if (!this.message.messageBody) {
        this.message.messageBody = body
        this.oldMessageTemplate = templateId
        return
      }

      const confirmChange = confirm('メッセージがすでに入力されています。テンプレートの内容に置き換えてよろしいですか?')
      if (!confirmChange) {
        this.messageTemplate = this.oldMessageTemplate
        return
      }
      this.oldMessageTemplate = templateId
      this.message.messageBody = body
    },

    /**
     * change mention template
     * @param {number} templateId
     */
    onChangeMentionTemplate(templateId: number) {
      const currentTemplate = this.messageTemplates.find(template => template.id === templateId)
      const body = currentTemplate.body

      if (!this.message.mentionBody) {
        this.message.mentionBody = body
        this.oldMentionTemplate = templateId
        return
      }

      const confirmChange = confirm('メッセージがすでに入力されています。テンプレートの内容に置き換えてよろしいですか?')
      if (!confirmChange) {
        this.mentionTemplate = this.oldMentionTemplate
        return
      }
      this.oldMentionTemplate = templateId
      this.message.mentionBody = body
    },

    /**
     * アップロードプログレスを取得
     * @param {string} filename
     */
    getUploadProgress(filename: string, type: string) {
      return new Promise(resolve => {
        const timer = setInterval(async () => {
          const progress = await api.get(`/twitter_uploads/progresses?filename=${filename}`)

          if (progress.data) {
            clearInterval(timer)
            resolve(progress.data)
          }

          if (progress.error) {
            if (progress.error.statusCode === 400) {
              this.$notify({
                title: '画像・動画アップロードに失敗しました。',
                message:
                  '詳しくは<a target="_blank" href="https://help-atelu.comnico.jp/available-image-and-video">こちら</a>をご確認ください。',
                customClass: 'danger',
                dangerouslyUseHTMLString: true,
                duration: 5000
              })
            } else {
              this.$notify({
                title: '画像・動画アップロードに失敗しました。',
                message: '恐れ入りますが、時間をおいて再度お試しください。',
                customClass: 'danger',
                duration: 5000
              })
            }

            clearInterval(timer)
            resolve(null)
          }
        }, 3000)

        if (type === 'mention') {
          this.uploadMentionTimer = timer
        } else {
          this.uploadMessageTimer = timer
        }
      })
    },

    /**
     * メンションのメディアファイル削除
     */
    removeMentionMedia() {
      this.message.mentionMedia = null
    },

    /**
     * ダイレクトメッセージのメディアファイル削除
     */
    removeMessageMedia() {
      this.message.messageMedia = null
    },

    /**
     * メンションカードを削除
     */
    removeMentionCard() {
      this.message.mentionCard = null
    },

    /**
     * リセット
     * @param {string} field
     */
    resetNumber(field: string) {
      setTimeout(() => {
        this.message[field] = 1
      }, 100)
    },

    /**
     * フォームをリセット
     */
    resetForm() {
      this.message = {
        id: '',
        name: '',
        messageType: this.messageType,
        winnerCount: 1,
        winningRate: 0.01,
        winningRateChangeType: 'none',
        dailyWinnerCount: 0,
        dailyWinnerChangeTime: '00:00',
        elapsedRate: 1,
        elapsedWinningRate: 1,
        weightCount: 1,
        entryKeyword: [],
        commentKeyword: [],
        replyType: 'mention',
        mentionBody: '',
        messageBody: '',
        mentionMedia: null,
        messageMedia: null,
        mentionCard: null,
        isDailyLottery: 0,
        excludeMessageMultiWinner: 1,
        excludeOtherMessageMultiWinner: 1,
        isPublicReply: 0,
        mentionMediaType: 'none',
        messageMediaType: 'none'
      }

      this.errors = {
        notInputStartDate: false,
        lessThanCurrentTime: false,
        lessThanStartTime: false,
        notInputEndDate: false
      }

      this.mentionCardOptions = {
        campaign_id: null,
        keyword: '',
        card_type: 'all',
        media_type: 'image',
        next_cursor: ''
      }

      this.$v.$reset()
      this.startDate = ''
      this.startTime = ''
      this.endDate = ''
      this.endTime = ''
      this.mentionCardId = ''
      this.timeout = null
      this.uploadingMentionMedia = false
      this.uploadingMessageMedia = false
      // テンプレート
      this.oldMentionTemplate = ''
      this.mentionTemplate = ''
      this.oldMessageTemplate = ''
      this.messageTemplate = ''
    },

    isImage(url: string) {
      const reg = /[.](jpeg|png|gif|pjpeg|jpg)$/i
      return reg.test(url)
    }
  },
  validations: {
    message: {
      name: {
        required
      }
    }
  }
})
</script>
